import React, { useRef, useState, useEffect } from "react";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";

import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/favicon.png";
import { useGetAllTicketsQuery, useFiatDetailsMutation, useListEnquiryQuery } from "../redux/features/user/userApi";
import { hasPermission, getPermissionLevel, isAdmin } from "../utils/permissionUtils";

const Navbar = () => {
  const [nav, setNav] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // "history" | "management" | null
  const [openMobileDropdown, setOpenMobileDropdown] = useState(null); // "history" | "management" | null
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const navRef = useRef(null);

  // Fetch tickets to determine if there are any currently raised/open tickets
  const { data: ticketsData } = useGetAllTicketsQuery();
  const { data: enquiryData } = useListEnquiryQuery();

  const openTicketsCount = React.useMemo(() => {
    const list = ticketsData?.data || ticketsData?.tickets || [];
    if (!Array.isArray(list)) return 0;

    return list.filter((E) => E.status === false).length

  }, [ticketsData]);


  const openEnquiryCount = React.useMemo(() => {
    const list = enquiryData?.data || [];
    if (!Array.isArray(list)) return 0;

    return list.filter((E) => E.status == 2).length

  }, [enquiryData]);


  // Fetch fiat transactions to get pending count
  const [fetchFiatDetails, { data: fiatData }] = useFiatDetailsMutation();

  const pendingFiatCount = React.useMemo(() => {
    const list = fiatData?.data || [];
    if (!Array.isArray(list)) return 0;

    return list.filter((transaction) => transaction.status === 2).length;
  }, [fiatData]);

  // Fetch fiat data on component mount
  React.useEffect(() => {
    fetchFiatDetails("deposit");
  }, [fetchFiatDetails]);

  const items = [
    {
      type: "link",
      id: "dashboard",
      label: "Dashboard",
      url: "/",
      permission: { resource: "user_details", action: "read" },
    },
    {
      type: "dropdown",
      id: "history",
      label: "History",
      children: [
        {
          id: "subscribe-history",
          label: "Stake History",
          url: "/subscribe",
          permission: { resource: "stake_history", action: "read" },
        },
        {
          id: "fiat-history",
          label: "Subscription History",
          url: "/fiat",
          permission: { resource: "subscribe_history", action: "read" },
        },
      ],
    },
    {
      type: "dropdown",
      id: "management",
      label: "Management",
      children: [
        {
          id: "ticket-management",
          label: "Ticket Management",
          url: "/tickets",
          permission: { resource: "ticket_managemenet", action: "read" },
        },
        {
          id: "enquiry",
          label: "Enquiry",
          url: "/enquiry",
          permission: { resource: "enquery_managemenet", action: "read" },
        },
        {

          id: "upload",
          label: "Upload Data",
          url: "/upload",
          permission: { resource: "upload_data", action: "write" },
        },
        {

          id: "subadmin",
          label: "Subadmin",
          url: "/subadmin",
          adminOnly: true,
        },
      ],
    },

  ];

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      if (!navRef.current) return;
      if (!navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const shouldShowItem = (item) => {
    // Admin-only links
    if (item.adminOnly && !isAdmin()) return false;

    // Permission-based links
    if (item.permission && !hasPermission(item.permission.resource, item.permission.action)) {
      return false;
    }

    // Dropdown: show if any child is visible
    if (item.type === "dropdown") {
      const visibleChildren = (item.children || []).filter((c) => shouldShowItem({ ...c, type: "link" }));
      return visibleChildren.length > 0;
    }

    return true;
  };

  const getTitle = (permission) => {
    const isAdminUser = isAdmin();
    if (isAdminUser) return "Admin - Full Access";
    if (!permission?.resource) return "";
    const level = getPermissionLevel(permission.resource);
    if (level === "read") return "Read Only Access";
    if (level === "write") return "Read & Write Access";
    return "";
  };

  return (
    <div className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-gradient-to-r from-[#dddee0] via-[#6d91cb] to-[#2563eb] shadow-2xl' : 'bg-gradient-to-r from-[#dddee0] via-[#6d91cb] to-[#2563eb]'
      } border-b border-blue-900`}>
      <div ref={navRef} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <img
              src={logo}
              className="h-16 w-auto rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 "
              alt="Logo"
            />
          </Link>

          {/* Desktop Navigation */}
          <ul className="hidden md:flex items-center space-x-4 lg:space-x-8">
            {items.map((item) => {
              if (!shouldShowItem(item)) return null;

              if (item.type === "dropdown") {
                const isOpen = openDropdown === item.id;
                const visibleChildren = (item.children || []).filter((c) => shouldShowItem({ ...c, type: "link" }));

                return (
                  <li key={item.id} className="relative">
                    <button
                      type="button"
                      onClick={() => setOpenDropdown(isOpen ? null : item.id)}
                      className="text-white hover:text-blue-300 px-3 py-2 rounded-md text-sm lg:text-base font-medium transition-colors duration-200 flex items-center gap-2"
                      aria-haspopup="menu"
                      aria-expanded={isOpen}
                    >
                      {item.label}
                      {item.id === "history" && pendingFiatCount > 0 && (
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                        </span>
                      )}
                      
                      {item.id === "management" && openEnquiryCount > 0 && (
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                        </span>
                      )}
                      <FaChevronDown className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                    </button>

                    {isOpen && (
                      <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-xl border border-blue-900/30 bg-white overflow-hidden">
                        <div className="py-2">
                          {visibleChildren.map((child) => (
                            <Link
                              key={child.id}
                              to={child.url}
                              onClick={() => setOpenDropdown(null)}
                              className="flex items-center justify-between gap-2 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-800 transition-colors"
                              title={getTitle(child.permission)}
                            >
                              <span>{child.label}</span>
                              {child.id === "ticket-management" && openTicketsCount > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold">
                                  {openTicketsCount}
                                </span>
                              )}
                              {child.id === "fiat-history" && pendingFiatCount > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-xs font-bold">
                                  {pendingFiatCount}
                                </span>
                              )}

                              {child.id === "enquiry" && openEnquiryCount > 0 && (
                                <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full bg-blue-600 text-white text-xs font-bold">
                                  {openEnquiryCount}
                                </span>
                              )}      

                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </li>
                );
              }

              return (
                <li key={item.id}>
                  <Link
                    to={item.url}
                    className="text-white hover:text-blue-300 px-3 py-2 rounded-md text-sm lg:text-base font-medium transition-colors duration-200 flex items-center gap-1"
                    title={getTitle(item.permission)}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
            <li>
              <button
                onClick={handleLogout}
                className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-orange-700 hover:to-indigo-700 text-white px-4 py-2 rounded-md text-sm lg:text-base font-semibold shadow transition-colors duration-200 border border-blue-800"
              >
                Logout
              </button>
            </li>
          </ul>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setNav(!nav)}
              className="text-white hover:text-blue-300 focus:outline-none p-2"
              aria-label="Toggle menu"
            >
              {nav ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className={`md:hidden transition-all duration-300 ease-in-out ${nav ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        } overflow-hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gradient-to-br from-[#1e293b] via-[#334155] to-[#2563eb] border-t border-blue-900">
          {items.map((item) => {
            if (!shouldShowItem(item)) return null;

            if (item.type === "dropdown") {
              const isOpen = openMobileDropdown === item.id;
              const visibleChildren = (item.children || []).filter((c) => shouldShowItem({ ...c, type: "link" }));

              return (
                <div key={item.id} className="w-full">
                  <button
                    type="button"
                    onClick={() => setOpenMobileDropdown(isOpen ? null : item.id)}
                    className="w-full text-white hover:bg-blue-700 px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 flex items-center justify-between"
                    aria-haspopup="menu"
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-center gap-2">
                      <span>{item.label}</span>
                      {item.id === "history" && pendingFiatCount > 0 && (
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
                        </span>
                      )}
                    </div>
                    <FaChevronDown className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                  </button>

                  {isOpen && (
                    <div className="pl-3 mt-1 space-y-1">
                      {visibleChildren.map((child) => (
                        <Link
                          key={child.id}
                          to={child.url}
                          className="flex items-center justify-between text-white/95 hover:bg-blue-700 px-3 py-2 rounded-md text-base font-medium transition-colors duration-200"
                          onClick={() => {
                            setNav(false);
                            setOpenMobileDropdown(null);
                          }}
                          title={getTitle(child.permission)}
                        >
                          <span>{child.label}</span>
                          {child.id === "ticket-management" && openTicketsCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">
                              {openTicketsCount}
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.id}
                to={item.url}
                className="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-base font-medium transition-colors duration-200 flex items-center justify-between"
                onClick={() => setNav(false)}
                title={getTitle(item.permission)}
              >
                <span>{item.label}</span>
              </Link>
            );
          })}
          <button
            onClick={() => {
              handleLogout();
              setNav(false);
              setOpenMobileDropdown(null);
            }}
            className="w-full text-left text-white hover:bg-blue-700 block px-3 py-2 rounded-md text-base font-medium transition-colors duration-200"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default Navbar;